import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'

export interface ZoneConfig {
  threshold_hours: number | null
  type: 'fixed' | 'auto'
  color: string
  opacity: number
  label: string
}

export interface RadarZones {
  panic_zone: ZoneConfig
  middle_zone: ZoneConfig
  outer_zone: ZoneConfig
}

export interface RadarZoneEditorProps {
  value: RadarZones | null
  onChange: (value: RadarZones) => void
  disabled?: boolean
}

const DEFAULT_ZONES: RadarZones = {
  panic_zone: { threshold_hours: 2, type: 'fixed', color: '#ef4444', opacity: 0.2, label: 'PANIKZONE' },
  middle_zone: { threshold_hours: 48, type: 'fixed', color: '#f97316', opacity: 0.15, label: 'MITTELZONE' },
  outer_zone: { threshold_hours: null, type: 'auto', color: '#94a3b8', opacity: 0.1, label: 'AUßENZONE' },
}

const COLOR_OPTIONS = [
  { value: '#ef4444', label: 'Rot' },
  { value: '#f97316', label: 'Orange' },
  { value: '#eab308', label: 'Gelb' },
  { value: '#22c55e', label: 'Grün' },
  { value: '#3b82f6', label: 'Blau' },
  { value: '#94a3b8', label: 'Grau' },
]

const ZONE_LABELS: Record<keyof RadarZones, { title: string; description: string; didactic: string; icon: string }> = {
  panic_zone: {
    title: 'Panikzone',
    description: 'Kritische Anfragen — sofortige Aktion nötig',
    didactic: 'Kritische Anfragen — sofortige Aktion nötig. Anfragen weniger als X Stunden bis zur Lektion gelten als kritisch und werden rot angezeigt.',
    icon: '🚨',
  },
  middle_zone: {
    title: 'Mittelzone',
    description: 'Mittelfristige Anfragen — noch planbar, aber Aufmerksamkeit nötig',
    didactic: 'Mittelfristige Anfragen — noch planbar, aber Aufmerksamkeit nötig. Anfragen zwischen Panik- und Mittelzone-Grenze werden orange angezeigt.',
    icon: '⚠️',
  },
  outer_zone: {
    title: 'Außenzone',
    description: 'Langfristige Anfragen — informativ, kein Druck',
    didactic: 'Langfristige Anfragen — informativ, kein Druck. Anfragen außerhalb der Mittelzone. Leer = bis Zeitraum-Ende. Werden dezent angezeigt.',
    icon: '📍',
  },
}

export function RadarZoneEditor({ value, onChange, disabled = false }: RadarZoneEditorProps) {
  const zones = value || DEFAULT_ZONES
  const [error, setError] = useState<string | null>(null)

  const handleZoneChange = (zoneKey: keyof RadarZones, field: keyof ZoneConfig, fieldValue: string | number | null) => {
    const newZones = {
      ...zones,
      [zoneKey]: {
        ...zones[zoneKey],
        [field]: fieldValue,
      },
    }
    
    // Validierung
    const panicHours = newZones.panic_zone.threshold_hours || 0
    const middleHours = newZones.middle_zone.threshold_hours || 48
    const outerHours = newZones.outer_zone.threshold_hours
    
    if (panicHours >= middleHours) {
      setError('Panikzone-Schwelle muss kleiner als Mittelzone-Schwelle sein')
    } else if (outerHours !== null && middleHours >= outerHours) {
      setError('Mittelzone-Schwelle muss kleiner als Außenzone-Schwelle sein')
    } else {
      setError(null)
    }
    
    onChange(newZones)
  }

  const renderZoneCard = (zoneKey: keyof RadarZones) => {
    const zone = zones[zoneKey]
    const meta = ZONE_LABELS[zoneKey]
    
    return (
      <div key={zoneKey} className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="flex items-start gap-3 mb-4">
          <span className="text-2xl">{meta.icon}</span>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{meta.title}</h3>
            <p className="text-sm text-gray-500">{meta.description}</p>
            <p className="text-xs text-gray-600 mt-2 italic">{meta.didactic}</p>
          </div>
        </div>
        
        <div className="space-y-3">
          {/* Threshold Hours */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schwellenwert (Stunden)
            </label>
            <input
              type="number"
              value={zone.threshold_hours ?? ''}
              onChange={e => {
                const val = e.target.value === '' ? null : Number(e.target.value)
                handleZoneChange(zoneKey, 'threshold_hours', val)
              }}
              disabled={disabled}
              placeholder="Auto (leer = bis Zeitraum-Ende)"
              min={0}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
            {zoneKey === 'outer_zone' && (
              <p className="text-xs text-gray-500 mt-1">Leer = Auto (bis Zeitraum-Ende). Wert muss größer als Mittelzone sein.</p>
            )}
          </div>
          
          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Typ
            </label>
            <select
              value={zone.type}
              onChange={e => handleZoneChange(zoneKey, 'type', e.target.value as 'fixed' | 'auto')}
              disabled={disabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 disabled:cursor-not-allowed"
            >
              <option value="fixed">Fixiert</option>
              <option value="auto">Automatisch</option>
            </select>
          </div>
          
          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Farbe
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={zone.color}
                onChange={e => handleZoneChange(zoneKey, 'color', e.target.value)}
                disabled={disabled}
                className="w-20 h-10 border border-gray-300 rounded-lg cursor-pointer disabled:cursor-not-allowed"
              />
              <select
                value={zone.color}
                onChange={e => handleZoneChange(zoneKey, 'color', e.target.value)}
                disabled={disabled}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                {COLOR_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Opacity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Opazität: {Math.round(zone.opacity * 100)}%
            </label>
            <input
              type="range"
              value={zone.opacity}
              onChange={e => handleZoneChange(zoneKey, 'opacity', Number(e.target.value))}
              disabled={disabled}
              min={0}
              max={1}
              step={0.05}
              className="w-full accent-indigo-600"
            />
          </div>
          
          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anzeige-Label
            </label>
            <input
              type="text"
              value={zone.label}
              onChange={e => handleZoneChange(zoneKey, 'label', e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {renderZoneCard('panic_zone')}
        {renderZoneCard('middle_zone')}
        {renderZoneCard('outer_zone')}
      </div>
      
      {/* Preview Diagram */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium mb-3">Vorschau</h4>
        <div className="relative w-full max-w-md mx-auto">
          {/* Outer zone */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              backgroundColor: zones.outer_zone.color,
              opacity: zones.outer_zone.opacity,
            }}
          />
          {/* Middle zone */}
          <div
            className="absolute top-[15%] left-[15%] w-[70%] h-[70%] rounded-full"
            style={{
              backgroundColor: zones.middle_zone.color,
              opacity: zones.middle_zone.opacity,
            }}
          />
          {/* Panic zone */}
          <div
            className="absolute top-[35%] left-[35%] w-[30%] h-[30%] rounded-full"
            style={{
              backgroundColor: zones.panic_zone.color,
              opacity: zones.panic_zone.opacity,
            }}
          />
          {/* Labels */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-xs font-medium" style={{ color: zones.panic_zone.color }}>
                {zones.panic_zone.label}
              </span>
            </div>
          </div>
          <div className="absolute top-[20%] left-1/2 -translate-x-1/2">
            <span className="text-xs font-medium" style={{ color: zones.middle_zone.color }}>
              {zones.middle_zone.label}
            </span>
          </div>
          <div className="absolute top-[5%] left-1/2 -translate-x-1/2">
            <span className="text-xs font-medium text-gray-600">
              {zones.outer_zone.label}
            </span>
          </div>
          {/* Aspect ratio placeholder */}
          <div className="w-full aspect-square" />
        </div>
        {/* Legend */}
        <div className="mt-4 flex justify-center gap-4 text-xs text-gray-600">
          <span>🔴 Panic = ≤ {zones.panic_zone.threshold_hours}h</span>
          <span>🟠 Middle = {zones.panic_zone.threshold_hours}-{zones.middle_zone.threshold_hours}h</span>
          <span>⚪ Outer = &gt; {zones.middle_zone.threshold_hours}h</span>
        </div>
      </div>
      
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-700">
          <strong>Tipp:</strong> Die Schwellenwerte definieren, wann welche Zone aktiv ist. 
          Beispiel: Panikzone bei 2 Stunden vor der Lektion, Mittelzone bei 48 Stunden, Außenzone immer.
        </p>
      </div>
    </div>
  )
}
